/**
 * Web Push subscription helpers.
 *
 * Flow:
 *  1. registerSW() — registers /sw.js (run once on app boot).
 *  2. enablePush() — asks for permission, gets VAPID pubkey, subscribes via
 *     PushManager, sends the subscription to the backend.
 *  3. disablePush() — unsubscribes locally and tells the backend.
 *  4. pushStatus() — returns "granted" | "denied" | "default" | "unsupported".
 */
import api from "./api";

const PUSH_SUPPORTED =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export function isPushSupported() {
  return PUSH_SUPPORTED;
}

export function pushPermission() {
  if (!PUSH_SUPPORTED) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function registerSW() {
  if (!PUSH_SUPPORTED) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js");
    return reg;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.warn("SW register failed:", err);
    return null;
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export async function getSubscription() {
  if (!PUSH_SUPPORTED) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/**
 * Asks for notification permission, subscribes, and POSTs the subscription
 * to the backend. Returns true on success.
 */
export async function enablePush() {
  if (!PUSH_SUPPORTED) throw new Error("Tu navegador no soporta notificaciones push.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Permiso denegado");
  }
  const reg = (await navigator.serviceWorker.getRegistration()) || (await registerSW());
  if (!reg) throw new Error("No se pudo registrar el service worker");

  const { data } = await api.get("/push/vapid-public-key");
  if (!data?.key) throw new Error("Falta clave VAPID del servidor");

  // If already subscribed, reuse.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.key),
    });
  }
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
  });
  return true;
}

function uint8ArrayEqual(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * Make sure the current PushSubscription is bound to the *current* VAPID
 * public key of the server. If they differ, the push service will reject
 * every send with HTTP 403 (VapidPkHashMismatch). We auto-heal by tearing
 * the stale subscription down and creating a fresh one.
 *
 * Safe to call on every app boot / profile mount — does nothing when the
 * keys already match or when there's no subscription.
 */
export async function ensureFreshSubscription() {
  if (!PUSH_SUPPORTED) return { changed: false, reason: "unsupported" };
  if (Notification.permission !== "granted") return { changed: false, reason: "no-permission" };
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return { changed: false, reason: "no-sw" };
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { changed: false, reason: "no-sub" };

    const { data } = await api.get("/push/vapid-public-key");
    if (!data?.key) return { changed: false, reason: "no-server-key" };
    const serverKey = urlBase64ToUint8Array(data.key);
    const subKey = sub.options?.applicationServerKey;
    if (!subKey) return { changed: false, reason: "no-sub-key" };
    const subArr = new Uint8Array(subKey);

    if (uint8ArrayEqual(subArr, serverKey)) {
      return { changed: false, reason: "match" };
    }

    // Mismatch — re-subscribe transparently.
    if (process.env.NODE_ENV !== "production") {
      console.info("VAPID key mismatch detected — re-subscribing transparently.");
    }
    await disablePush();
    await enablePush();
    return { changed: true, reason: "vapid-mismatch" };
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.warn("ensureFreshSubscription failed:", err?.message);
    return { changed: false, reason: "error", error: err?.message };
  }
}

export async function disablePush() {
  if (!PUSH_SUPPORTED) return;
  const sub = await getSubscription();
  if (sub) {
    const { endpoint } = sub.toJSON();
    try { await api.post("/push/unsubscribe", { endpoint }); } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("push unsubscribe failed:", err?.message);
    }
    try { await sub.unsubscribe(); } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("PushSubscription.unsubscribe failed:", err?.message);
    }
  }
}

export async function sendTestPush() {
  const { data } = await api.get("/push/test");
  return data; // { sent, failed, removed, statuses: [{host, status, error}] }
}

/**
 * Full re-subscribe cycle: tears down the local PushSubscription, tells
 * the backend to forget it, then subscribes again with the current VAPID
 * public key. Used when the existing subscription is bound to a stale
 * key (typical symptom: test returns 403/`VapidPkHashMismatch`).
 */
export async function resubscribe() {
  if (!PUSH_SUPPORTED) throw new Error("Tu navegador no soporta notificaciones push.");
  await disablePush();
  return enablePush();
}

// Listen to SW messages (e.g. notificationclick → navigate)
export function attachSWNavigationBridge(navigateFn) {
  if (!PUSH_SUPPORTED) return;
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event?.data?.type === "navigate" && event.data.url) {
      try { navigateFn(event.data.url); } catch (err) {
        if (process.env.NODE_ENV !== "production") console.warn("SW navigate bridge failed:", err?.message);
      }
    }
  });
}
